import { isDemoMode, db } from '../firebase';
import type { Attestation, User } from '../types';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';

const demoAttestationsKey = 'demo_attestations';

const getDemoAttestations = (): Attestation[] => {
  const stored = localStorage.getItem(demoAttestationsKey);
  if (stored) {
    try {
      return JSON.parse(stored).map((att: any) => ({
        ...att,
        createdAt: new Date(att.createdAt),
        attestedAt: att.attestedAt ? new Date(att.attestedAt) : null,
        lastAttestedVersion: att.lastAttestedVersion ?? null,
        changeSummaries: att.changeSummaries || [],
      }));
    } catch {
      return [];
    }
  }
  return [];
};

const saveDemoAttestations = (attestations: Attestation[]) => {
  localStorage.setItem(demoAttestationsKey, JSON.stringify(attestations));
};

const parseAttestation = (docId: string, data: any): Attestation => ({
  id: docId,
  compoundId: data.compoundId || docId,
  userId: data.userId,
  userName: data.userName || '',
  userEmail: data.userEmail || '',
  articleId: data.articleId,
  articleTitle: data.articleTitle,
  articleVersion: data.articleVersion,
  lastAttestedVersion: data.lastAttestedVersion ?? null,
  linkedQualIds: data.linkedQualIds || [],
  changeSummaries: data.changeSummaries || [],
  attested: data.attested || false,
  attestedAt: data.attestedAt?.toDate() || null,
  createdAt: data.createdAt?.toDate() || new Date(),
  deletedAt: data.deletedAt?.toDate() || null,
  deletedBy: data.deletedBy || null,
});

export const attestationService = {
  async getAll(includeDeleted = false): Promise<Attestation[]> {
    if (isDemoMode) {
      const all = getDemoAttestations();
      return includeDeleted ? all : all.filter((a) => !a.deletedAt);
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const attestationsRef = collection(db, 'attestations');
      const snapshot = await getDocs(attestationsRef);
      const all = snapshot.docs.map((d) => parseAttestation(d.id, d.data()));
      return includeDeleted ? all : all.filter((a) => !a.deletedAt);
    } catch (error) {
      console.error('Error getting attestations:', error);
      throw error;
    }
  },

  async getForUser(userId: string, includeDeleted = false): Promise<Attestation[]> {
    if (isDemoMode) {
      const all = getDemoAttestations().filter((att) => att.userId === userId);
      return includeDeleted ? all : all.filter((a) => !a.deletedAt);
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const attestationsRef = collection(db, 'attestations');
      const q = query(attestationsRef, where('userId', '==', userId));
      const snapshot = await getDocs(q);
      const all = snapshot.docs.map((d) => parseAttestation(d.id, d.data()));
      return includeDeleted ? all : all.filter((a) => !a.deletedAt);
    } catch (error) {
      console.error('Error getting user attestations:', error);
      throw error;
    }
  },

  async getPendingForUser(userId: string): Promise<Attestation[]> {
    if (isDemoMode) {
      return getDemoAttestations().filter(
        (att) => att.userId === userId && !att.attested && !att.deletedAt
      );
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const attestationsRef = collection(db, 'attestations');
      const q = query(
        attestationsRef,
        where('userId', '==', userId),
        where('attested', '==', false)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map((d) => parseAttestation(d.id, d.data()))
        .filter((a) => !a.deletedAt);
    } catch (error) {
      console.error('Error getting pending attestations:', error);
      throw error;
    }
  },

  /**
   * Create or update attestation requirements for an article update.
   * Uses compound key userId_articleId (no version) so each user has at most
   * one attestation record per article. If a pending attestation already exists,
   * it's updated to the new version. If the user already attested a previous
   * version, a new pending attestation replaces it (preserving lastAttestedVersion).
   * Users with excludeFromProgress are skipped.
   */
  async createForArticleUpdate(
    articleId: string,
    articleTitle: string,
    articleVersion: number,
    linkedQualIds: string[],
    changeSummary: string,
    users: User[]
  ): Promise<void> {
    // Filter out observers
    const eligibleUsers = users.filter((u) => !u.excludeFromProgress && !u.deactivated && u.role !== 'outside_observer');

    if (isDemoMode) {
      const attestations = getDemoAttestations();
      const now = new Date();

      for (const user of eligibleUsers) {
        const compoundId = `${user.uid}_${articleId}`;
        const existingIdx = attestations.findIndex((att) => att.compoundId === compoundId);

        if (existingIdx >= 0) {
          const existing = attestations[existingIdx];
          const lastAttested = existing.attested ? existing.articleVersion : existing.lastAttestedVersion;
          // Accumulate change summaries (reset if user had attested)
          const prevSummaries = existing.attested ? [] : (existing.changeSummaries || []);
          attestations[existingIdx] = {
            ...existing,
            articleTitle,
            articleVersion,
            linkedQualIds,
            lastAttestedVersion: lastAttested,
            changeSummaries: changeSummary ? [...prevSummaries, `v${articleVersion}: ${changeSummary}`] : prevSummaries,
            attested: false,
            attestedAt: null,
            createdAt: now,
          };
        } else {
          attestations.push({
            id: compoundId,
            compoundId,
            userId: user.uid,
            userName: user.displayName || user.email,
            userEmail: user.email,
            articleId,
            articleTitle,
            articleVersion,
            lastAttestedVersion: null,
            linkedQualIds,
            changeSummaries: changeSummary ? [`v${articleVersion}: ${changeSummary}`] : [],
            attested: false,
            attestedAt: null,
            createdAt: now,
          });
        }
      }

      saveDemoAttestations(attestations);
      return;
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      for (const user of eligibleUsers) {
        const compoundId = `${user.uid}_${articleId}`;
        const docRef = doc(db, 'attestations', compoundId);

        // Check existing to preserve lastAttestedVersion and accumulate change summaries
        const existing = await getDoc(docRef);
        let lastAttestedVersion: number | null = null;
        let prevSummaries: string[] = [];

        if (existing.exists()) {
          const data = existing.data();
          lastAttestedVersion = data.attested ? data.articleVersion : (data.lastAttestedVersion ?? null);
          // Keep accumulated summaries if user hasn't attested yet; reset if they had
          prevSummaries = data.attested ? [] : (data.changeSummaries || []);
        }

        const changeSummaries = changeSummary
          ? [...prevSummaries, `v${articleVersion}: ${changeSummary}`]
          : prevSummaries;

        await setDoc(docRef, {
          compoundId,
          userId: user.uid,
          userName: user.displayName || user.email,
          userEmail: user.email,
          articleId,
          articleTitle,
          articleVersion,
          lastAttestedVersion,
          linkedQualIds,
          changeSummaries,
          attested: false,
          attestedAt: null,
          createdAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error('Error creating attestations:', error);
      throw error;
    }
  },

  async attest(attestationId: string): Promise<void> {
    if (isDemoMode) {
      const attestations = getDemoAttestations();
      const att = attestations.find((a) => a.id === attestationId);
      if (att) {
        att.attested = true;
        att.attestedAt = new Date();
        att.lastAttestedVersion = att.articleVersion;
        saveDemoAttestations(attestations);
      }
      return;
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const docRef = doc(db, 'attestations', attestationId);
      const existing = await getDoc(docRef);
      const currentVersion = existing.exists() ? existing.data().articleVersion : null;

      await updateDoc(docRef, {
        attested: true,
        attestedAt: serverTimestamp(),
        lastAttestedVersion: currentVersion,
      });
    } catch (error) {
      console.error('Error attesting:', error);
      throw error;
    }
  },

  /** Soft-delete all pending attestations for a given library item (e.g. when soft-deleting the item) */
  async dismissForArticle(articleId: string, deletedBy: string): Promise<void> {
    if (isDemoMode) {
      const attestations = getDemoAttestations();
      for (const att of attestations) {
        if (att.articleId === articleId && !att.attested) {
          att.deletedAt = new Date();
          att.deletedBy = deletedBy;
        }
      }
      saveDemoAttestations(attestations);
      return;
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const attestationsRef = collection(db, 'attestations');
      const q = query(
        attestationsRef,
        where('articleId', '==', articleId),
        where('attested', '==', false)
      );
      const snapshot = await getDocs(q);
      const updates = snapshot.docs.map((d) =>
        updateDoc(d.ref, { deletedAt: Timestamp.now(), deletedBy })
      );
      await Promise.all(updates);
    } catch (error) {
      console.error('Error dismissing attestations for article:', error);
      throw error;
    }
  },

  /** Admin: soft-delete a single attestation */
  async dismiss(attestationId: string, deletedBy: string): Promise<void> {
    if (isDemoMode) {
      const attestations = getDemoAttestations();
      const att = attestations.find((a) => a.id === attestationId);
      if (att) {
        att.deletedAt = new Date();
        att.deletedBy = deletedBy;
      }
      saveDemoAttestations(attestations);
      return;
    }

    if (!db) throw new Error('Firebase not initialized');

    try {
      const docRef = doc(db, 'attestations', attestationId);
      await updateDoc(docRef, { deletedAt: Timestamp.now(), deletedBy });
    } catch (error) {
      console.error('Error dismissing attestation:', error);
      throw error;
    }
  },
};
